import { Component, OnInit, Input, ElementRef, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { StandpuntService, IAgendapuntStandpuntInfo, IStandpuntStatus } from '../standpunt.service';
import { replace } from '../../shared/immutable';
import { createCmdFactory } from '@cevi/rxjscommands';
import { Observable, Subject, ReplaySubject } from 'rxjs';
import { mergeMap, map, delay } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { NgIf, NgClass, NgFor, AsyncPipe } from '@angular/common';

interface IBepaalStandpuntVm {
  punt: IAgendapuntStandpuntInfo;
  subpunten: IAgendapuntStandpuntInfo[];
  editStandpuntStatus: IStandpuntStatus;
  editPunt: IAgendapuntStandpuntInfo;
  zittingId: number;
  relativeTo?: ElementRef;
}

const emptyVm: IBepaalStandpuntVm = {
  punt: null as IAgendapuntStandpuntInfo,
  subpunten: [],
  editStandpuntStatus: null,
  editPunt: null,
  zittingId: 0
};

interface IStandpuntChange {
  vm: IBepaalStandpuntVm;
  subpunt?: IAgendapuntStandpuntInfo;
  newStatus: number;
}

@Component({
    selector: 'app-bepaal-standpunt',
    templateUrl: './bepaal-standpunt.component.html',
    styleUrls: ['./bepaal-standpunt.component.scss'],
    standalone: true,
    imports: [NgIf, FormsModule, NgClass, NgFor, AsyncPipe],
    providers: [StandpuntService]
})
export class BepaalStandpuntComponent implements OnInit {

  vm$: Observable<IBepaalStandpuntVm>;
  vm: IBepaalStandpuntVm;

  standpuntFromInputSubj = new ReplaySubject<IAgendapuntStandpuntInfo[]>();
  zittingFromInputSubj = new ReplaySubject<number>();
  relvativeToFromInputSubj = new Subject<ElementRef>();
  editPuntOpmerkingSubj = new Subject<IAgendapuntStandpuntInfo>();
  modifyOpmerkingSubj = new Subject<IBepaalStandpuntVm>();
  modifySubpuntOpmerkingSubj = new Subject<IBepaalStandpuntVm>();
  setStatusPuntSubj = new Subject<IStandpuntChange>();
  setStatusSubpuntSubj = new Subject<IStandpuntChange>();
  closeOpmerkingSubj = new Subject<boolean>();

  constructor(private standpuntService: StandpuntService) {
    const cmdFactory = createCmdFactory<IBepaalStandpuntVm>();
    cmdFactory.addMapAction(this.zittingFromInputAction$, 'zittingFromInput');
    cmdFactory.addMapAction(this.standpuntenFromInputAction$(), 'standpuntenFromInput');
    cmdFactory.addMapAction(this.updateStatusStandpuntAction$(), 'updateStandpunt');
    cmdFactory.addMapAction(this.updateStatusSubpuntAction$(), 'updateStatusSubpunt');
    cmdFactory.addMapAction(this.editPuntOpmerkingAction$, 'editPuntOpmerking');
    cmdFactory.addMapAction(this.modifyOpmerkingAction$, 'modifyOpmerking');
    cmdFactory.addMapAction(this.modifySubpuntOpmerkingAction$, 'modifySubpuntOpmerking');
    cmdFactory.addMapAction(this.closeOpmerkingAction$, 'closeOpmerking');
    this.vm$ = cmdFactory.merge(emptyVm);
  }

  ngOnInit() { }

  @Input() set standpunten(value: IAgendapuntStandpuntInfo[]) {
    this.standpuntFromInputSubj.next(value);
  }
  @Input() set zittingId(value: number) {
    this.zittingFromInputSubj.next(value);
  }

  @Output() closeStandpunt: EventEmitter<IAgendapuntStandpuntInfo> = new EventEmitter<IAgendapuntStandpuntInfo>();


  relativeToFromInput = {
    name: 'relativeFromInput',
    source$: this.relvativeToFromInputSubj,
    process: (vm: IBepaalStandpuntVm, relativeTo: ElementRef) => {
      return { ...vm, relativeTo };
    }
  };

  zittingFromInputAction$ = this.zittingFromInputSubj.pipe(
    map(zittingId => (vm: IBepaalStandpuntVm) => ({ ...vm, zittingId }))
  );

  standpuntenFromInputAction$ = () => this.standpuntFromInputSubj.pipe(
    map(standpunten => (vm: IBepaalStandpuntVm) => this.mapStandpuntenToVm(vm, standpunten))
  )

  updateStatusStandpuntAction$ = () => this.setStatusPuntSubj.pipe(
    map(change => ({ ...change, newStandpunt: { ...change.vm.punt.Standpunt, Status: change.newStatus } })),
    mergeMap((change) => this.standpuntService.update(change.vm.zittingId, change.vm.punt.Id, change.newStandpunt)),
    map(standpunten => (vm: IBepaalStandpuntVm) => this.mapStandpuntenToVm(vm, standpunten))
  )

  updateStatusSubpuntAction$ = () => this.setStatusSubpuntSubj.pipe(
    map(change => ({ ...change, newStandpunt: { ...change.subpunt.Standpunt, Status: change.newStatus } })),
    mergeMap((change) => this.standpuntService.update(change.vm.zittingId, change.vm.punt.Id, change.newStandpunt)),
    map(standpunten => (vm: IBepaalStandpuntVm) => this.mapStandpuntenToVm(vm, standpunten))
  )

  editPuntOpmerkingAction$ = this.editPuntOpmerkingSubj.pipe(
    map(punt => (vm: IBepaalStandpuntVm) => {
      if (punt.Standpunt.Status !== 1) { return vm; }
      return {
        ...vm,
        editPunt: punt,
        editStandpuntStatus: { ...punt.Standpunt }
      };
    })
  );

  modifyOpmerkingAction$ = this.modifyOpmerkingSubj.pipe(
    mergeMap(vm => this.standpuntService.updateCommentaar(vm.editPunt, vm.editStandpuntStatus)),
    map(punten => (vm: IBepaalStandpuntVm) => ({
      ...vm,
      editPunt: null,
      editStandpuntStatus: null,
      punt: punten[0]
    }))
  );

  modifySubpuntOpmerkingAction$ = this.modifySubpuntOpmerkingSubj.pipe(
    mergeMap(vm => this.standpuntService.updateCommentaar(vm.editPunt, vm.editStandpuntStatus)),
    map(subpunten => (vm: IBepaalStandpuntVm) => {
      return {
        ...vm,
        editPunt: null,
        editStandpuntStatus: null,
        subpunten: replace(vm.subpunten, vm.editPunt, subpunten[0])
      };
    })
  );

  closeOpmerkingAction$ = this.closeOpmerkingSubj.pipe(
    map(_ => (vm: IBepaalStandpuntVm) => ({
      ...vm,
      editPunt: null,
      editStandpuntStatus: null
    }))
  );

  mapUpdatedOpmerkingResultToVm = (vm: IBepaalStandpuntVm, _: boolean) => {
    return {
      ...vm,
      editPunt: null,
      editStandpuntStatus: null,
      punt: { ...vm.editPunt, Standpunt: vm.editStandpuntStatus }
    };
  }


  mapStandpuntenToVm = (vm: IBepaalStandpuntVm, standpunten: IAgendapuntStandpuntInfo[]) => {
    if (!standpunten) { return vm; }
    const standpuntenMapped = standpunten.map(s => ({
      ...s,
      Standpunt: s.Standpunt
        || (s.HeeftSubPunten
          ? null
          : { StandpuntId: 0, AgendapuntId: s.Id, Commentaar: null, Status: 0 })
    }));
    return {
      ...vm,
      standpunten: standpuntenMapped,
      punt: standpuntenMapped.filter(s => !s.SubPunt)[0],
      subpunten: standpuntenMapped.filter(s => s.SubPunt)
    };
  }

  onClose(punt: IAgendapuntStandpuntInfo) {
    this.closeStandpunt.emit(punt);
  }

  setStatusPunt(vm: IBepaalStandpuntVm, newStatus: number) {
    this.setStatusPuntSubj.next({ vm, newStatus });
  }
  setStatusSubpunt(vm: IBepaalStandpuntVm, subpunt: IAgendapuntStandpuntInfo, newStatus: number) {
    this.setStatusSubpuntSubj.next({ vm, subpunt, newStatus });
  }

}
